/**
 * Shared finance primitives used by more than one calculator.
 *
 * Ported unchanged from Realiform.
 */

/**
 * Level payment on a fully-amortising loan.
 *
 * Handles the 0% case, which the standard formula divides by zero on — worth
 * caring about because seller financing at 0% is common in small deals.
 */
export function monthlyPayment(
  principal: number,
  annualRatePct: number,
  termYears: number
): number {
  if (principal <= 0 || termYears <= 0) return 0;
  const n = termYears * 12;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / n;
  const factor = Math.pow(1 + r, n);
  return (principal * r * factor) / (factor - 1);
}

/** Total interest paid over the life of the loan. */
export function totalInterest(
  principal: number,
  annualRatePct: number,
  termYears: number
): number {
  if (principal <= 0 || termYears <= 0) return 0;
  return monthlyPayment(principal, annualRatePct, termYears) * termYears * 12 - principal;
}

/**
 * Remaining balance after `monthsElapsed` payments — used to show equity built
 * by the amortisation itself, which small-business buyers routinely forget is
 * part of their return.
 */
export function remainingBalance(
  principal: number,
  annualRatePct: number,
  termYears: number,
  monthsElapsed: number
): number {
  if (principal <= 0 || termYears <= 0) return 0;
  const n = termYears * 12;
  const m = Math.min(Math.max(monthsElapsed, 0), n);
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal * (1 - m / n);
  const pmt = monthlyPayment(principal, annualRatePct, termYears);
  const growth = Math.pow(1 + r, m);
  return Math.max(principal * growth - pmt * ((growth - 1) / r), 0);
}
